import React, { useEffect, useState } from "react";
import moment from "moment";
import { toast } from "react-toastify";

import Modal from "../../../components/Modal";
import Button from "../../../components/Button";
import Icon from "../../../components/Icon";
import Input from "../../../components/Input";
import InputDate from "../../../components/InputDate";
import InputSelect from "../../../components/InputSelect";
import { Loading } from "../../../components/Loading";
import { PlanFeatureUpgradeModal } from "../../../components/PlanFeatureUpgradeModal";

import api from "../../../services/api";
import { renderError } from "../../../helpers/errors";
import { canUseOpenFinance, hasPermission } from "../../../services/auth";

export const OPEN_FINANCE_LABELS = {
    active: 'Banco conectado',
    pending: 'Aguardando autorização',
    revoked: 'Desconectada',
};

const bankKey = bank => `${bank.code}|${bank.name}`;

/**
 * Entrada do Open Finance nas telas do financeiro (BDM-1049). Sem o recurso no
 * plano vira convite de upgrade; com ele, mostra o estado da conexão da conta
 * e abre o modal que conecta, sincroniza e desconecta.
 */
export const OpenFinanceButton = ({ bankAccount, onChange }) => {
    const [showUpgrade, setShowUpgrade] = useState(false);
    const [showModal, setShowModal] = useState(false);

    if (!canUseOpenFinance()) {
        return (
            <>
                <button className={`open-finance-trigger`} onClick={() => setShowUpgrade(true)}>
                    <Icon name={`connected`} size={16} />
                    <span>Open Finance</span>
                    <span className={`trigger-tag`}>Upgrade</span>
                </button>
                <PlanFeatureUpgradeModal feature={`open_finance`} show={showUpgrade} close={() => setShowUpgrade(false)} />
            </>
        );
    }

    if (!bankAccount || bankAccount.type !== 'bank') {
        return null;
    }

    const status = bankAccount.open_finance_status;

    return (
        <>
            <button className={`open-finance-trigger status-${status ?? 'none'}`} onClick={() => setShowModal(true)}>
                <Icon name={status === 'active' ? `connected` : `disconnected`} size={16} />
                <span>{OPEN_FINANCE_LABELS[status] ?? 'Conectar ao banco'}</span>
            </button>
            <ModalOpenFinance
                show={showModal}
                close={() => setShowModal(false)}
                bankAccount={bankAccount}
                onChange={onChange}
            />
        </>
    );
};

export const ModalOpenFinance = ({ show, close, bankAccount, onChange }) => {
    const [loading, setLoading] = useState(true);
    const [acting, setActing] = useState(null);
    const [state, setState] = useState(null);
    const [banks, setBanks] = useState([]);
    const [confirmDisconnect, setConfirmDisconnect] = useState(false);

    const [bank, setBank] = useState(null);
    const [agency, setAgency] = useState('');
    const [agencyDigit, setAgencyDigit] = useState('');
    const [accountNumber, setAccountNumber] = useState('');
    const [accountDigit, setAccountDigit] = useState('');
    const [since, setSince] = useState(moment().subtract(30, 'days').format('YYYY-MM-DD'));

    const canManage = hasPermission('manage_finance');

    useEffect(() => {
        if (!show || !bankAccount) return;

        // A aba de contas usa um modal só para todas: o que ficou da conta
        // anterior iria no POST da próxima.
        setState(null);
        setBank(null);
        setSince(moment().subtract(30, 'days').format('YYYY-MM-DD'));
        setConfirmDisconnect(false);
        getStatus();

        if (banks.length === 0) {
            api.get(`/finance-open-finance/banks`).then(res => setBanks(res.data.resources)).catch(renderError);
        }
    }, [show, bankAccount?.id]);

    useEffect(() => {
        if (!state) return;

        const account = state.resource;
        setAgency(account.agency ?? '');
        setAgencyDigit(account.agency_digit ?? '');
        setAccountNumber(account.account_number ?? '');
        setAccountDigit(account.account_digit ?? '');
        if (account.open_finance_since) setSince(moment(account.open_finance_since).format('YYYY-MM-DD'));
    }, [state?.resource?.id]);

    useEffect(() => {
        if (!state || bank || banks.length === 0) return;

        const current = banks.find(item => item.code === state.resource.bank_code);
        if (current) setBank(bankKey(current));
    }, [state, banks]);

    const getStatus = () => {
        setLoading(true);

        api.get(`/finance-open-finance/${bankAccount.id}`)
            .then(res => setState(res.data))
            .catch(renderError)
            .then(() => setLoading(false));
    };

    const act = (name, request, onSuccess) => {
        setActing(name);

        request.then(res => {
            setState(res.data);
            onChange && onChange(res.data.resource);
            onSuccess && onSuccess(res.data);
        }).catch(renderError).then(() => setActing(null));
    };

    const connect = () => {
        const selected = banks.find(item => bankKey(item) === bank);

        act('connect', api.post(`/finance-open-finance/${bankAccount.id}/connect`, {
            bank_code: selected?.code ?? null,
            agency,
            agency_digit: agencyDigit || null,
            account_number: accountNumber,
            account_digit: accountDigit || null,
            since,
        }), data => {
            if (data.resource.open_finance_link) {
                window.open(data.resource.open_finance_link, '_blank');
            }
        });
    };

    const check = () => {
        act('check', api.post(`/finance-open-finance/${bankAccount.id}/check`), data => {
            if (data.resource.open_finance_status === 'active') {
                toast.success('Conta conectada. Os lançamentos chegam na próxima sincronização.');
            } else {
                toast.info('O banco ainda não confirmou a autorização. Conclua no app ou internet banking e tente de novo.');
            }
        });
    };

    const sync = () => {
        act('sync', api.post(`/finance-open-finance/${bankAccount.id}/sync`), () => {
            toast.success('Sincronização pedida. Os lançamentos aparecem na conciliação assim que o banco responder.');
        });
    };

    const disconnect = () => {
        act('disconnect', api.post(`/finance-open-finance/${bankAccount.id}/disconnect`), () => {
            setConfirmDisconnect(false);
            toast.success('Conta desconectada do banco');
        });
    };

    const account = state?.resource;
    const status = account?.open_finance_status ?? null;
    const lastStatement = state?.last_statement;
    const bankOptions = banks.map(item => ({ label: `${item.code} · ${item.name}`, value: bankKey(item) }));

    const renderForm = () => (
        <>
            <p className={`open-finance-intro`}>
                {status === 'revoked'
                    ? 'A autorização desta conta foi encerrada. Confira os dados e autorize de novo no banco.'
                    : 'Confira os dados da conta. Em seguida, o banco abre numa nova aba para você autorizar o acesso ao extrato.'}
            </p>
            <div className="row">
                <div className="col-12">
                    <InputSelect label={`Banco`} options={bankOptions} value={bank} change={setBank} noOptionsText={`Banco não disponível no Open Finance`} />
                </div>
            </div>
            <div className="row">
                <div className="col-4">
                    <Input label={`Agência`} value={agency} change={setAgency} />
                </div>
                <div className="col-2">
                    <Input label={`Dígito`} value={agencyDigit} change={setAgencyDigit} />
                </div>
                <div className="col-4">
                    <Input label={`Conta`} value={accountNumber} change={setAccountNumber} />
                </div>
                <div className="col-2">
                    <Input label={`Dígito`} value={accountDigit} change={setAccountDigit} />
                </div>
            </div>
            <div className="row">
                <div className="col-6">
                    <InputDate label={`Buscar lançamentos desde`} format={`YYYY-MM-DD`} value={since} change={setSince} />
                </div>
            </div>
        </>
    );

    const renderPending = () => (
        <div className={`open-finance-step`}>
            <Icon name={`clock`} size={22} />
            <div>
                <strong>Falta autorizar no banco</strong>
                <p>
                    Abra a autorização, escolha o banco e confirme o acesso no app ou internet banking.
                    Depois volte aqui e clique em <strong>Já autorizei</strong>.
                </p>
                <p className={`open-finance-note`}>Os primeiros lançamentos podem levar até 24 horas para o banco liberar.</p>
            </div>
        </div>
    );

    const renderActive = () => (
        <>
            <ul className={`open-finance-facts`}>
                <li>
                    <span>Conectada desde</span>
                    <strong>{account.open_finance_connected_at ? moment(account.open_finance_connected_at).format('DD/MM/YYYY') : '—'}</strong>
                </li>
                <li>
                    <span>Última sincronização</span>
                    <strong>{account.open_finance_synced_at ? moment(account.open_finance_synced_at).format('DD/MM/YYYY [às] HH:mm') : 'Ainda não sincronizou'}</strong>
                </li>
                <li>
                    <span>Sincronização automática</span>
                    <strong>Todo dia, às 7h e às 14h</strong>
                </li>
            </ul>

            {state.processing &&
                <p className={`open-finance-note`}>Buscando lançamentos no banco. Eles entram na conciliação assim que o banco responder.</p>
            }
            {!state.processing && lastStatement?.status === 'failed' &&
                <p className={`open-finance-note error`}>A última busca falhou: {lastStatement.status_reason}</p>
            }

            {confirmDisconnect &&
                <div className={`open-finance-confirm`}>
                    <p>
                        Desconectar encerra a autorização no banco e para a sincronização desta conta.
                        Os lançamentos já trazidos continuam na conciliação.
                    </p>
                    <div className={`confirm-actions`}>
                        <Button type={`secondary`} size={`small`} text={`Cancelar`} action={() => setConfirmDisconnect(false)} />
                        <Button type={`delete`} size={`small`} text={`Desconectar`} action={disconnect} loading={acting === 'disconnect'} />
                    </div>
                </div>
            }
        </>
    );

    const renderFooter = () => {
        if (!account || !canManage) return null;

        if (status === 'active') {
            return (
                <>
                    {!confirmDisconnect &&
                        <Button type={`secondary`} size={`small`} text={`Desconectar`} action={() => setConfirmDisconnect(true)} />
                    }
                    <Button
                        type={`primary`}
                        size={`small`}
                        svg={`refresh`}
                        text={state.next_sync_at ? `Disponível às ${moment(state.next_sync_at).format('HH:mm')}` : `Sincronizar agora`}
                        action={sync}
                        loading={acting === 'sync'}
                        disabled={Boolean(state.next_sync_at) || state.processing}
                    />
                </>
            );
        }

        if (status === 'pending') {
            return (
                <>
                    <Button type={`secondary`} size={`small`} svg={`external-link`} text={`Abrir autorização`} action={() => window.open(account.open_finance_link, '_blank')} disabled={!account.open_finance_link} />
                    <Button type={`primary`} size={`small`} text={`Já autorizei`} action={check} loading={acting === 'check'} />
                </>
            );
        }

        return (
            <Button
                type={`primary`}
                size={`small`}
                svg={`connected`}
                text={`Conectar ao banco`}
                action={connect}
                loading={acting === 'connect'}
                disabled={!bank || !agency || !accountNumber}
            />
        );
    };

    return (
        <Modal
            className={`open-finance-modal`}
            title={bankAccount ? `Open Finance · ${bankAccount.name}` : 'Open Finance'}
            show={show}
            close={close}
            footer={renderFooter()}
        >
            <div className="section">
                <Loading show={loading} />
                {!loading && account && status === 'active' && renderActive()}
                {!loading && account && status === 'pending' && renderPending()}
                {!loading && account && status !== 'active' && status !== 'pending' && renderForm()}
            </div>
        </Modal>
    );
};

export default OpenFinanceButton;
